import React, { useState, useEffect } from 'react';
import { useGoals } from '../../contexts/GoalContext';
import { getTasksByGoal } from '../../services/firebaseStorage';
import { format } from 'date-fns';
import { parseLocalDate } from '../../utils/dateUtils';

const GoalProgress = ({ goal, onClose }) => {
    const { getProgress } = useGoals();
    const [progress, setProgress] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [progressData, tasksData] = await Promise.all([
                    getProgress(goal.id),
                    getTasksByGoal(goal.id, goal.calendarId)
                ]);
                setProgress(progressData);
                setTasks(tasksData);
            } catch (error) {
                console.error('Error loading goal progress:', error);
            } finally {
                setLoading(false);
            }
        };

        if (goal) {
            loadData();
        }
    }, [goal, getProgress]);

    const getStatusColor = (status) => {
        const colors = {
            'not-started': 'text-gray-600 dark:text-gray-400',
            'in-progress': 'text-blue-600 dark:text-blue-400',
            'completed': 'text-green-600 dark:text-green-400',
            'blocked': 'text-red-600 dark:text-red-400'
        };
        return colors[status] || colors['not-started'];
    };

    const getStatusLabel = (status) => {
        const labels = {
            'not-started': 'Not Started',
            'in-progress': 'In Progress',
            'completed': 'Completed',
            'blocked': 'Blocked'
        };
        return labels[status] || 'Not Started';
    };

    if (!goal) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div
                    className="fixed inset-0 bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                ></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                Goal Progress: {goal.title}
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                                aria-label="Close"
                            >
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {loading ? (
                            <div className="text-center py-8">
                                <p className="text-gray-500 dark:text-gray-400">Loading progress...</p>
                            </div>
                        ) : (
                            <>
                                {/* Overall Progress */}
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Overall Progress
                                        </span>
                                        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                            {progress?.completionPercentage || 0}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                                        <div
                                            className="bg-gradient-to-r from-blue-500 to-green-500 h-4 rounded-full transition-all"
                                            style={{ width: `${progress?.completionPercentage || 0}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Task Breakdown */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                                        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                            {progress?.totalTasks || 0}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">Total Tasks</p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                            {progress?.completedTasks || 0}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">Completed</p>
                                    </div>
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                            {progress?.inProgressTasks || 0}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">In Progress</p>
                                    </div>
                                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
                                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                            {progress?.blockedTasks || 0}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">Blocked</p>
                                    </div>
                                </div>

                                {/* Timeline Information */}
                                {goal.targetDate && (
                                    <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                        <p className="text-sm text-gray-700 dark:text-gray-300">
                                            <span className="font-medium">Target Date:</span>{' '}
                                            {format(new Date(goal.targetDate), 'MMMM d, yyyy')}
                                        </p>
                                    </div>
                                )}

                                {/* Associated Tasks List */}
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                                        Associated Tasks ({tasks.length})
                                    </h4>
                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                        {tasks.length === 0 ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                                No tasks associated with this goal yet.
                                            </p>
                                        ) : (
                                            tasks.map(task => (
                                                <div
                                                    key={task.id}
                                                    className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <p className={`text-sm font-medium ${task.completed || task.status === 'completed'
                                                                    ? 'line-through text-gray-500 dark:text-gray-500'
                                                                    : 'text-gray-900 dark:text-gray-100'
                                                                }`}>
                                                                {task.task}
                                                            </p>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                    Due: {format(parseLocalDate(task.dueDate), 'MMM d, yyyy')}
                                                                </span>
                                                                <span className={`text-xs font-medium ${getStatusColor(task.status || 'not-started')}`}>
                                                                    {getStatusLabel(task.status || 'not-started')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GoalProgress;
